"use client"
import { api } from "@/components/TRPCProvider"
import { Avatar } from "@/components/ui/Avatar"
import { Tooltip } from "@/components/ui/Tooltip"
import type { TourStep } from "@/components/ui/tour"
import { createFullName } from "@/lib/helpers/text"
import { createAssetUrl } from "@/lib/s3"
import { Folder } from "lucide-react"
import NextLink from "next/link"

export function TourWrapper({ children, title }: { children: React.ReactNode; title: string }) {
  const { data: accountManager } = api.user.accountManager.useQuery()

  return (
    <div className="relative w-full text-white">
      <p className="pb-4 font-bold">{title}</p>

      <div className="flex items-start space-x-4 text-sm">
        <Tooltip label={accountManager ? createFullName(accountManager) : "Jemma Betterton"}>
          <Avatar
            src={accountManager ? createAssetUrl(accountManager.avatar) : "/jemma.png"}
            size={30}
            className="size-7 flex-shrink-0"
          />
        </Tooltip>
        <div>{children}</div>
      </div>
    </div>
  )
}

const HOME = [
  {
    target: "tour-home-tour",
    step: (
      <TourWrapper title="Welcome to TrendWatching!">
        <p>
          You can always come back and click this icon for a tour-by-step tour if you need guidance with finding your way through
          TrendWatching. Stay tuned, we're adding tours to more pages every week.
        </p>
        <br />
        <p>To begin with, let's take a quick look at what you can do with this page!</p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-explore",
    step: (
      <TourWrapper title="Explore page">
        <p>
          The explore page is your starting point, spotlighting the latest content added by our trend analysts, daily. Including
          stats, the trend of the week, and recommended innovations based on your interests.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-recent",
    step: (
      <TourWrapper title="Recently viewed">
        <p>
          Use the recently viewed section to find where you saw that golden piece of content before you lose it.{" "}
          <b>Look no further</b>, just jump right back into the last innovation, report, stat, or trend you've read.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-coming-soon",
    step: (
      <TourWrapper title="Find out what is coming soon">
        <p>
          This is where you can find exclusive announcements from TrendWatching including exciting product launches, content
          pieces, and events.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-chat",
    step: (
      <TourWrapper title="Ask an Analyst">
        <p>
          Here is where you can get in direct contact with one of our in-house Trend Analysts to ask a question about the
          platform's intelligence or tools. Fast replies are guaranteed <b>(yes, by a real person!)</b>.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-global-search",
    step: (
      <TourWrapper title="Global search">
        <p>
          Use the global search bar when you need a quick scan of our entire database of trends and insights including stats,
          innovations, and reports. Enter any keyword or topic and get results sorted by the content type, be sure to select{" "}
          <b>view all</b> to see more results.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-profile",
    step: (
      <TourWrapper title="Profile">
        <p>
          Here is where you can edit your profile, select your interests to personalize your TrendWatching experience, see who's
          on your team and manage your account.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-projects",
    step: (
      <TourWrapper title="My projects">
        <p>
          Here is where you can manage your personal and collaborative projects. Collate the most inspiring innovations, stats,
          trends, and reports into customizable folders, share them with your team or download them all in one package.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-notifications",
    step: (
      <TourWrapper title="Notifications">
        <p>
          Here you can find notifications about your account, new content, and your collaborative projects! For example, you will
          be notified as soon as new a Trend Report, Sub-Trend, select Innovations, and Stats are published, as well as when
          someone joins your team or shares a project with you.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-invite",
    step: (
      <TourWrapper title="Invite colleagues">
        <p>
          This is where you can invite more colleagues and grant access to your entire team. No worries, it's included in your
          plan! Just enter their emails or share the invite link to start collaborating with them.
        </p>
      </TourWrapper>
    ),
  },
] as TourStep[]

const INNOVATIONS = [
  {
    target: "tour-innovation-list",
    step: (
      <TourWrapper title="Welcome to the innovation database!">
        <p>
          Browse, find, and get inspired by 32,000+ innovations setting new consumer expectations segmented by keywords, industry,
          region, business goal, and more. Updated weekly to inform you of the most up-to-date concepts, products, services,
          experiences, and campaigns from around the world.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-innovation-item",
    step: (
      <TourWrapper title="Innovation preview">
        <p>
          Here is an example of the most recent innovation uploaded to the database. Each innovation preview contains important
          information to help you quickly understand its relevance.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-innovation-item-sub-trends",
    step: (
      <TourWrapper title="Related Sub-Trends">
        <p>
          Here is where you can find the innovation's related sub-trends which detail related drivers and opportunities. Diving
          into the related sub-trend will help you to get a better understanding of how to apply the innovation to your own
          business.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-innovation-item-business-goal",
    step: (
      <TourWrapper title="The innovation's main business goal">
        <p>
          Each innovation is tagged to a business goal, revealing the deeper intention behind a brand's concept, product, service,
          experience, or campaign. Use these for inspiration to advance your own business goals in new ways.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-innovation-item-ctas",
    step: (
      <TourWrapper title="Share, save or download">
        <p>
          Here is where you can share any innovation with a team member, save it to a personal or shared project or download it
          instantly as a PowerPoint.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-innovation-filters",
    step: (
      <TourWrapper title="Filter the database">
        <p>
          There are endless opportunities for inspiration in the database, so use the filters to narrow down your search with
          keywords, business objectives, trends, and more.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-innovation-search",
    step: (
      <TourWrapper title="Search the database">
        <p>You can also search through the innovations with the keyword, brand, or topic you have in mind.</p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-innovation-toggle-view",
    step: (
      <TourWrapper title="Switch the innovation page view">
        <p>
          We all prefer reading information differently. Switch up your view of innovations by toggling between the card or list
          view.
        </p>
      </TourWrapper>
    ),
  },
] as TourStep[]

const INTERESTS = [
  {
    target: "tour-interests-industries",
    step: (
      <TourWrapper title="Interests">
        <p>
          Select the industries, countries, keywords and innovation types you want to see more of so that TrendWatching can
          recommend even more useful innovations and insights.
        </p>
      </TourWrapper>
    ),
  },
] as TourStep[]

const PROJECT = [
  {
    target: "tour-project-items",
    step: (
      <TourWrapper title="Your project items">
        <p>
          If you have added any content to your project, this is where you can browse through it, see who added it and when. Start
          adding relevant content to your project by clicking this project icon{" "}
          <Folder className="inline-flex size-4 self-center" /> on any report, trend, stat, or innovation you find inspiring from
          anywhere on the platform.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-project-filter-content",
    step: (
      <TourWrapper title="Filter your project items">
        <p>
          Too many amazing insights and content saved? Use this filter to easily sort everything you have saved so far by content
          type.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-project-edit",
    step: (
      <TourWrapper title="Edit your project">
        <p>
          This is where you can edit your Project's name and description, turn on and off notifications and even add a custom
          cover photo.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-project-download",
    step: (
      <TourWrapper title="Download your project">
        <p>Download your project entirely in one zip file, depending on the size it might take a minute.</p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-project-share",
    step: (
      <TourWrapper title="Share your project">
        <p>
          Share your project with anyone on your team and start collaborating. Once they are added they will be able to contribute
          with any content they find inspiring.
        </p>
        <br />
        <p>
          Is your colleague not on TrendWatching yet? Just enter their email to invite them to join{" "}
          <b>(it's included in your plan!)</b>.
        </p>
      </TourWrapper>
    ),
  },
] as TourStep[]

const MEGATRENDS = [
  {
    target: "tour-megatrends-tab",
    step: (
      <TourWrapper title="Get familiar with our Mega-Trends">
        <p>
          You've made it to the Mega-Trend level, <b>the center</b> of our trend framework. Make sure you don't miss the full
          picture of the consumer world and toggle between the tabs to explore the other levels of our trend framework.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-megatrends-desc",
    step: (
      <TourWrapper title="The Mega-Trends in action">
        <p>
          Our Mega-Trends represent the big consumer shifts of the decade relevant across all industries and regions, perfect for
          strategic guidance. So how can you apply Mega-Trends? Here are some ideas to get you started:
        </p>
        <br />
        <ol>
          <li>
            Create a top-level brand strategy (in combination with our{" "}
            <NextLink href={"/toolbox"} target="_blank" className="underline">
              CTR
            </NextLink>
            )
          </li>
          <li>Check how zeitgeist-fit your brand or your product portfolio is</li>
          <li>Explore new consumer segments your existing strategy might overlook</li>
        </ol>
      </TourWrapper>
    ),
  },
  {
    target: "tour-megatrends-filter-macrotrends",
    step: (
      <TourWrapper title="Filter by Macro-Trend">
        <p>
          Here you can filter Mega-Trends based on our Macro-Trends, to narrow down your search focused on the{" "}
          <b>big shifts moving the world</b> across society, technology, economy, ecology, and politics.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-megatrends-filter-industries",
    step: (
      <TourWrapper title="Filter by Industry">
        <p>
          Use this filter to find Mega-Trends that are most relevant for your industry. We suggest digging into non-relating
          industries for inspiration and insights as our Mega-Trends <b>don't live in industry silos</b>.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-megatrends-filter-basic-needs",
    step: (
      <TourWrapper title="Filter by Basic Human Needs">
        <p>
          This filter helps you find Mega-Trends that link to specific <b>consumer profiles</b> you might be working with. Our
          Basic Human Needs are loosely based on Maslow's Hierarchy of Needs of rarely changing human wants and desires.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-chat",
    step: (
      <TourWrapper title="Get trend guidance from an Analyst">
        <p>
          Lastly, if you're ever in doubt about our trends, how to find the most relevant ones, or how to apply them to your
          business, hit this icon (or use our Ask an Analyst service) to get in direct contact with one of our in-house Trend
          Analysts. Fast replies are guaranteed <b>(yes, by a real person!)</b>.
        </p>
      </TourWrapper>
    ),
  },
] as TourStep[]

const MACROTRENDS = [
  {
    target: "tour-macrotrends-tab",
    step: (
      <TourWrapper title="Get familiar with our Macro-Trends">
        <p>
          You've made it to the Macro-Trend level, the <b>top level</b> of our trend framework. Make sure you don't miss the full
          picture of the consumer world and toggle between the tabs to explore the other levels of our trend framework.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-macrotrends-desc",
    step: (
      <TourWrapper title="The Macro-Trends in action">
        <p>
          Macro-Trends are the long-term shifts that drive change around the globe, helping you{" "}
          <b>capture a big-picture overview</b> of where the world is headed. So how can you <b>apply Macro-Trends?</b> Here are
          some ideas to get you started:
        </p>
        <br />
        <ol>
          <li>Identify the key macro drivers of your market or industry</li>
          <li>Use macro trends for your long-term scenario planning</li>
          <li>Scope the environmental contexts your brand, products, or services are exposed to</li>
        </ol>
      </TourWrapper>
    ),
  },
  {
    target: "tour-macrotrends-filter-topic",
    step: (
      <TourWrapper title="Filter by topic">
        <p>
          Our Macro-trends are categorized into these five core themes. Use them to find the Macro-Trends most relevant to you
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-chat",
    step: (
      <TourWrapper title="Get trend guidance from an Analyst">
        <p>
          Lastly, if you're ever in doubt about our trends, how to find the most relevant ones, or how to apply them to your
          business, use our Ask an Analyst service to get in direct contact with one of our in-house Trend Analysts. Fast replies
          are guaranteed <b>(yes, by a real person!)</b>.
        </p>
      </TourWrapper>
    ),
  },
] as TourStep[]

const SUBTRENDS = [
  {
    target: "tour-subtrends-tab",
    step: (
      <TourWrapper title="Get familiar with our Sub-Trends">
        <p>
          You've made it to the Sub-Trend level, representing the most <b>actionable opportunities</b> that are easy to apply to
          your business. Make sure you don't miss the full picture of the consumer world and toggle between the tabs to explore
          the other levels of our trend framework.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-subtrends-desc",
    step: (
      <TourWrapper title="The Sub-Trends in action">
        <p>
          Sub-Trends are our most frequently updated trends based on <b>emerging consumer expectations</b>. So how can you apply
          Sub-Trends? Here are some ideas to get you started:
        </p>
        <br />
        <ol>
          <li>Create a new, on-trend product strategy and step into a new market</li>
          <li>Develop a creative marketing campaign</li>
          <li>Trendify your products and services by applying elements of our trend analysis to them</li>
        </ol>
      </TourWrapper>
    ),
  },
  {
    target: "tour-subtrends-search",
    step: (
      <TourWrapper title="Search Sub-Trends">
        <p>Use the search bar to explore trends relating to a topic or keyword that is at the top of your mind.</p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-subtrends-filter-macrotrends",
    step: (
      <TourWrapper title="Filter by Macro-Trend">
        <p>
          Here you can filter Sub-Trends based on our Macro-Trends, to narrow down your search focused on the{" "}
          <b>big shifts moving the world</b> across society, technology, economy, ecology, and politics.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-subtrends-filter-megatrends",
    step: (
      <TourWrapper title="Filter by Mega-Trend">
        <p>
          Mega-Trends sit at the center of our trend hierarchy and are used for an overall understanding of where{" "}
          <b>consumerism is heading</b>. Use these filters to find the top sub-trends related to the Mega-Trends most relevant to
          you.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-subtrends-filter-industries",
    step: (
      <TourWrapper title="Filter by Industry">
        <p>
          Use this filter to find Sub-Trends that are most relevant for your industry. We also suggest digging into non-relating
          industries for inspiration and insights as our Sub-Trends <b>don't live in industry silos</b>.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-subtrends-filter-basic-needs",
    step: (
      <TourWrapper title="Filter by Basic Human Needs">
        <p>
          This filter helps you find Sub-Trends that link to specific <b>consumer profiles</b> you might be working with. Our
          Basic Human Needs are loosely based on Maslow's Hierarchy of Needs of rarely changing human wants and desires.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-home-chat",
    step: (
      <TourWrapper title="Get trend guidance from an Analyst">
        <p>
          Lastly, if you're ever in doubt about our trends, how to find the most relevant ones, or how to apply them to your
          business, hit this icon to get in direct contact with one of our in-house Trend Analysts. Fast replies are guaranteed{" "}
          <b>(yes, by a real person!)</b>.
        </p>
      </TourWrapper>
    ),
  },
] as TourStep[]

const PROJECTS = [
  {
    target: "tour-projects-overview",
    step: (
      <TourWrapper title="Welcome to your projects page!">
        <p>
          Here is where you can manage your personal and collaborative projects. Collate the most inspiring innovations, stats,
          trends, and reports into customizable folders, share them with your team or download them all in one package.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-projects-new",
    step: (
      <TourWrapper title="Create a new project">
        <p>
          This is where you can create a new project. If you haven't made one yet, we encourage you to do so. It's easy, just give
          it a name and description, then start organizing your favorite content. If you want, you can share it with your
          colleagues too.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-projects-listview",
    step: (
      <TourWrapper title="Overview of all your projects">
        <p>
          Come back here to find an overview of all your projects including private and shared projects. Are you a super-user with
          too many projects to count? Don't fret just use the search bar to find it.
        </p>
      </TourWrapper>
    ),
  },
] as TourStep[]

const FILTERS = [
  {
    target: "tour-all-filters",
    step: (
      <TourWrapper title="All Filters">
        <p>
          Drill down on your specific query with ease. Try using one or more of these filters to help you get started. We
          recommend to be focused on the number of filters you use. Remember, sometimes less is more 😉
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-keyword-filters",
    step: (
      <TourWrapper title="Keywords">
        <p>
          The keyword filters are hand-picked by TrendWatching's Head of Research, and continuously updated for you to find
          inspiration from the most pressing and emerging topics of today.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-megatrend-filters",
    step: (
      <TourWrapper title="Mega-Trends & Sub-Trends">
        <p>
          Filter the database by any Mega-Trend and its Sub-Trends, to help you understand the deeper consumer expectation behind
          each innovation and see inspiring and motivating ideas in a new light.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-industry-region-filters",
    step: (
      <TourWrapper title="Industries & Regions">
        <p>
          Filter the database by industry or region to uncover the best-in-class expectation-setting innovations from any market.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-innovation-type-filters",
    step: (
      <TourWrapper title="Innovation Type">
        <p>
          Filter the database by innovation type and find inspiration from conceptual ideas to fully executed products, campaigns,
          and experiences you may want to implement on your own.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-business-goal-filters",
    step: (
      <TourWrapper title="Business Goals">
        <p>
          Filter the database by the most common business goals relating to product strategy, sustainability, growth, and more to
          get inspiration on how to take action on critical goals related to your specific organization or job role
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-analyst-pick-filters",
    step: (
      <TourWrapper title="Analyst Picks">
        <p>
          Use these filters to see innovations hand-picked by our Analysts, including additional deeper insight into how they are
          propelling consumer expectations to help you think out of the ordinary.
        </p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-my-filters",
    step: (
      <TourWrapper title="My Filters">
        <p>This is where you can find your saved filters and save time from having to search again.</p>
      </TourWrapper>
    ),
  },
  {
    target: "tour-recent-filters",
    step: (
      <TourWrapper title="Recent Filters">
        <p>
          Don't worry about forgetting the most successful combination of filters you have used so far. You can find all your
          recent filters used here.
        </p>
      </TourWrapper>
    ),
  },
] as TourStep[]

export const TOUR = {
  HOME,
  INNOVATIONS,
  INTERESTS,
  PROJECTS,
  PROJECT,
  MEGATRENDS,
  MACROTRENDS,
  SUBTRENDS,
  FILTERS,
}
